import {gql} from '@apollo/client';
import React from 'react';

import * as PolicyDAO from 'auth/PolicyDAO';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import notifications from 'notifications';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  SchemaPoliciesListingQuery,
  SchemaPoliciesListingQueryVariables,
} from './__generated/SchemaPoliciesListingQuery';
import PoliciesListing from './PoliciesListing';

export const SCHEMA_POLICIES_LISTING_QUERY = gql`
  query SchemaPoliciesListingQuery($limitOffsetInput: LimitOffsetInput!)
  @skipAudit
  @engComponent(name: PERMISSIONS) {
    schemaPolicyListing(limitOffsetInput: $limitOffsetInput) {
      schemaPolicies {
        id
        name
        permissions {
          readable
          writable
        }
      }
    }
  }
`;

// We only ever request the first 3 results because because end users are not able to add new schema policies.
// When we lift this restriction, we will need to add a proper paging UI.
const SCHEMA_POLICIES_LISTING_QUERY_VARIABLES: SchemaPoliciesListingQueryVariables = {
  limitOffsetInput: {
    limit: 3,
    offset: 0,
  },
};

export default function SchemaPoliciesListing(): JSX.Element | null {
  const result = useBenchlingQuery<SchemaPoliciesListingQuery, SchemaPoliciesListingQueryVariables>(
    SCHEMA_POLICIES_LISTING_QUERY,
    {variables: SCHEMA_POLICIES_LISTING_QUERY_VARIABLES}
  );
  if (result.type === QueryResultType.ERROR) {
    notifications.error('Could not load schema policies; try reloading the page.');
    return null;
  } else if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else {
    const policies = result.data.schemaPolicyListing.schemaPolicies;
    return (
      <PoliciesListing
        policies={policies}
        getPolicyURL={PolicyDAO.getSchemaPolicyURL}
        // Schema policies currently do not support user edits.
        deletePolicy={(string) => Promise.reject(new Error('Not implemented'))}
        clonePolicy={(string) => Promise.reject(new Error('Not implemented'))}
        reorderPolicies={(string) => Promise.reject(new Error('Not implemented'))}
      />
    );
  }
}
